import React, { useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslations, useForm } from "hooks";
import { getTranslation, CONSTANTS } from "common";
import {
  Box,
  Container,
  Grid,
  TextField,
  Typography,
  Button,
  Stack,
  Checkbox,
  FormControlLabel,
  Link as MUILink,
} from "@mui/material";
import { Link } from "react-router-dom";
import { Validation, isEmail, isRequired } from "hooks/useForm";
import { usersApi, fetchMe } from "api";
import { OAuth2, AuthSidebar } from "components";
import { AuthContext } from "contexts";
import { Step } from "components/LandingSidebar";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";

interface RegistrationForm {
  email: String;
  lastname: String;
  name: String;
}

const Registration = () => {
  const lang = navigator.language.split("-")[0] || "en";
  const privacyPolicyUrl = `/privacy-policy-${lang}.html`;
  // console.log(privacyPolicyUrl)
  const translations = useTranslations();
  const {
    actions: { setUser, setRegisterUser },
  } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jwt = searchParams.get("jwt");
  const emailParam = searchParams.get("email");
  const registrationTextFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "background.paper",
    },
    "& input:-webkit-autofill": {
      WebkitBoxShadow: (theme: any) => `0 0 0 100px ${theme.palette.background.paper} inset`,
      WebkitTextFillColor: (theme: any) => theme.palette.text.primary,
      caretColor: (theme: any) => theme.palette.text.primary,
      transition: "background-color 5000s ease-in-out 0s",
    },
  };

  const validations: Validation[] = [
    ({ email }) =>
      isRequired(email) || {
        email: getTranslation(translations, "register.fieldrequired"),
      },
    ({ email }) =>
      isEmail(email) || {
        email: getTranslation(translations, "register.valid.Error_after_at"),
      },
    ({ firstname }) =>
      isRequired(firstname) || {
        firstname: getTranslation(translations, "register.fieldrequired"),
      },
    ({ lastname }) =>
      isRequired(lastname) || {
        lastname: getTranslation(translations, "register.fieldrequired"),
      },
    ({ termsAccepted }) =>
      termsAccepted === true || {
        termsAccepted: getTranslation(translations, "register.termsrequired"),
      },
  ];

  const { values, changeHandler, errors, touched, isValid } = useForm(
    {
      email: emailParam ?? "",
      firstname: "",
      lastname: "",
      termsAccepted: false,
    },
    validations
  );

  const enterWithGoogle = async (token: string, registrated: boolean) => {
    try {
      sessionStorage.setItem(CONSTANTS.STORE_JWT, token);
      let result = await fetchMe(token);
      if (registrated) {
        setUser({
          jwt: token,
          userId: result.data.user.id,
          email: result.data.user.email,
          username: result.data.user.username,
          firstname: result.data.user.firstname,
          lastname: result.data.user.lastname,
          picture: result.data.user.picture,
          linkprofile: result.data.user.linkprofile,
          subscriptiontype: result.data.user.subscriptiontype,
          i18n: result.data.user.i18n,
          dob: result.data.user.dob,
          authenticationtype: result.data.user.authenticationtype,
        });
        navigate("/dashboard/organizations");
      } else {
        setRegisterUser({
          userId: result.data.user.id,
          jwt: token,
          ...result.data.user,
        });
        navigate("/organization");
      }
    } catch {
    } finally {
    }
  };

  const saveData = async (user: RegistrationForm) => {
    if (emailParam && jwt) {
      await usersApi.put(jwt, {
        user,
      });
      navigate("/login");
    } else {
      try {
        const result = await usersApi.post("", {
          user: {
            ...user,
            subscriptiontype: 0,
          },
        });
        setRegisterUser({
          userId: result.data.id,
          jwt: "",
          ...result.data,
        });
        navigate("/organization");
      } catch {
      } finally {
      }
    }
  };

  const steps: Step[] = [
    {
      number: 1,
      title: getTranslation(translations, "step_title.configure_organization"),
      description: getTranslation(translations, "step_description.configure_organization"),
    },
    {
      number: 2,
      title: getTranslation(translations, "step_title.design_and_publish"),
      description: getTranslation(translations, "step_description.design_and_publish"),
    },
    {
      number: 3,
      title: getTranslation(translations, "step_title.start_and_collaborate"),
      description: getTranslation(translations, "step_description.start_and_collaborate")
    },
    {
      number: 4,
      title: getTranslation(translations, "step_title.monitor_and_analyze"),
      description: getTranslation(translations, "step_description.monitor_and_analyze"),
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        background: "background.default",
        flexDirection: { xs: "column-reverse", md: "row" },
        // mr: { xs: 2, md: 46 }
      }}
    >
      <AuthSidebar
        title={getTranslation(translations, "how_it_works.title")}
        steps={steps}
      />

      {/* Right Section - Registration Form */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
          background: "background.paper",
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ maxWidth: 480, mx: "auto" }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 1,
                textAlign: "center",
              }}
            >
              {getTranslation(translations, "register.free.account")}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 4,
                textAlign: "center",
              }}
            >
              {getTranslation(translations, "register.no.credit.card.required")}
            </Typography>

            {/* {process.env.REACT_APP_ENV === "development" && */}
            <>
              <OAuth2 onGoogleConfirm={enterWithGoogle} showLoginButton={false} />
              <Box sx={{ position: "relative", mb: 3 }}>
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: 0,
                    right: 0,
                    height: "1px",
                    backgroundColor: "divider",
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    position: "relative",
                    textAlign: "center",
                    color: "text.secondary",
                    backgroundColor: "background.paper",
                    display: "inline-block",
                    px: 2,
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                >
                  {getTranslation(translations, "register.or.register.with.email")}
                </Typography>
              </Box>
            </>
            {/* } */}
            <Grid container component="form" spacing={4}>
              <Grid item xs={6}>
                <TextField
                  id="registration-name"
                  size="small"
                  label={getTranslation(
                    translations,
                    "register.registration.name.label"
                  )} fullWidth
                  sx={registrationTextFieldSx}
                  error={Boolean(touched.firstname && errors.firstname)}
                  helperText={touched.firstname && errors.firstname}
                  value={values.firstname}
                  onChange={({ target }) =>
                    changeHandler("firstname", target.value)
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id="registration-lastname"
                  size="small"
                  label={getTranslation(
                    translations,
                    "register.registration.lastname.label"
                  )}
                  fullWidth
                  sx={registrationTextFieldSx}
                  error={Boolean(touched.lastname && errors.lastname)}
                  helperText={touched.lastname && errors.lastname}
                  value={values.lastname}
                  onChange={({ target }) =>
                    changeHandler("lastname", target.value)
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  disabled={Boolean(emailParam)}
                  id="registration-email"
                  size="small"
                  label={getTranslation(
                    translations,
                    "register.registration.email.label"
                  )}
                  fullWidth
                  sx={registrationTextFieldSx}
                  error={Boolean(touched.email && errors.email)}
                  helperText={touched.email && errors.email}
                  value={values.email}
                  onChange={({ target }) =>
                    changeHandler("email", target.value)
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="condition-checkbox"
                      size="small"
                      checked={values.termsAccepted}
                      onChange={(e) => changeHandler("termsAccepted", e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {getTranslation(translations, "register.label.ahref_terms_and_conditions")} {""}
                      <MUILink href={privacyPolicyUrl} target="_blank"
                        rel="noopener noreferrer" variant="body2" color="primary" underline="hover">
                        {getTranslation(translations, "register.label.ahref_terms_and_conditions_link")}
                      </MUILink>.
                    </Typography>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FlowentiButton
                  id="registration-button"
                  variant="contained"
                  fullWidth
                  type="submit"
                  disabled={!isValid}
                  onClick={(e) => {
                    e.preventDefault();
                    saveData(values);
                  }}
                >
                  {getTranslation(translations, "register.free.account")} →
                </FlowentiButton>
              </Grid>
              <Grid item xs={12}>
                <FlowentiButton
                  component={Link}
                  to="/login"
                  variant="outlined"
                >
                  {getTranslation(translations, "register.label.ahref_login_have_account")}
                </FlowentiButton>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Registration;
